import { API_BASE_URL } from "../../utilities/constants";
import HttpInterface from "../../api/httpClient";

import {
  Location,
  CreateLocation,
  UpdateLocation,
} from "../../Models/Location";

import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";

import { PaginatedResponse } from "../../Models/Pagination";
import { buildAPIUrl } from "../../api/apiHelpers";

export class LocationsService {
  private http: HttpInterface;

  constructor() {
    this.http = new HttpInterface();
  }

  /**
   * Fetches paginated locations from the registry service API.
   *
   * @param {FetchParam} [params=defaultFetchParam] - Pagination and filter params.
   * @returns {Promise<PaginatedResponse<Location>>} Paginated list of locations.
   */
  all(
    params: FetchParam = defaultFetchParam
  ): Promise<PaginatedResponse<Location>> {
    return this.http.fetch<PaginatedResponse<Location>>(
      buildAPIUrl(`${API_BASE_URL}/registry-service/locations`, params),
      { method: "GET" }
    );
  }

  /**
   * Fetch a single location by its ID.
   *
   * @param {string} id - The location ID.
   * @returns {Promise<Location>} The requested location.
   */
  get(id: string): Promise<Location> {
    return this.http.fetch<Location>(
      `${API_BASE_URL}/registry-service/locations/${id}`,
      { method: "GET" }
    );
  }

  /**
   * Creates a new location.
   *
   * @param {CreateLocation} location - The data used to create the location.
   * @returns {Promise<Location>} The created location.
   */
  create(location: CreateLocation): Promise<Location> {
    return this.http.fetch<Location>(
      `${API_BASE_URL}/registry-service/locations`,
      {
        method: "POST",
        body: JSON.stringify(location),
      }
    );
  }

  /**
   * Updates an existing location.
   *
   * @param {UpdateLocation} location - Updated location data.
   * @returns {Promise<Location>} The updated location.
   */
  edit(location: UpdateLocation): Promise<Location> {
    return this.http.fetch<Location>(
      `${API_BASE_URL}/registry-service/locations/${location.id}`,
      {
        method: "PUT",
        body: JSON.stringify(location),
      }
    );
  }

  /**
   * Deletes a location.
   *
   * @param {string} id - The ID of the location to delete.
   * @returns {Promise<void>} Resolves on successful deletion.
   */
  delete(id: string): Promise<void> {
    return this.http.fetch<void>(
      `${API_BASE_URL}/registry-service/locations/${id}`,
      { method: "DELETE" }
    );
  }
}
